import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { API_BASE_URL } from "../config/apiBase";
import { getAuthUserHeader } from "../utils/userRoles";
import { buildCurriculumLessonMap } from "./tutorWorkflowData";

const API = API_BASE_URL;

function toTitle(week, lesson) {
  return `W${week}L${lesson}`;
}

function wordCount(text) {
  return String(text || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;
}

function safeText(value) {
  return String(value || "").trim();
}

function parseMaybeJson(value) {
  if (!value) return null;
  if (typeof value === "object") return value;
  if (typeof value !== "string") return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function formatSignalValue(value) {
  if (value === null || value === undefined) return "—";
  if (Array.isArray(value)) return value.filter(Boolean).join(", ") || "—";
  if (typeof value === "object") {
    const parts = [];
    if (typeof value.signal === "string" && value.signal.trim()) parts.push(value.signal.trim());
    if (typeof value.label === "string" && value.label.trim()) parts.push(value.label.trim());
    if (typeof value.status === "string" && value.status.trim()) parts.push(value.status.trim());
    if (value.present === true) parts.push("present");
    if (value.present === false) parts.push("missing");
    if (typeof value.detector_used === "string" && value.detector_used.trim()) parts.push(value.detector_used.trim());
    if (typeof value.count === "number") parts.push(`${value.count} items`);
    if (Array.isArray(value.spans)) parts.push(`${value.spans.length} spans`);
    if (Array.isArray(value.highlights)) parts.push(`${value.highlights.length} highlights`);
    if (Array.isArray(value.evidence)) parts.push(`${value.evidence.length} evidence items`);
    if (Array.isArray(value.quotes_by_signal)) parts.push(`${value.quotes_by_signal.length} quotes`);
    if (typeof value.text === "string" && value.text.trim()) parts.push(value.text.trim());
    return parts.length ? parts.join(" · ") : "—";
  }
  const text = String(value).trim();
  return text || "—";
}

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function titleCase(value) {
  return String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function normalizeSignalLabel(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const overrides = {
    SENSORY_DETAIL: "Sensory Detail",
    SPECIFIC_DETAIL: "Specific Detail",
    INTERNAL_THOUGHT: "Internal Thought",
    VERB_STRENGTH: "Verb Strength",
    EMOTION: "Emotion",
    ACTION: "Action",
    DIALOGUE: "Dialogue",
    REPETITION: "Repetition",
  };
  const upper = raw.toUpperCase();
  if (overrides[upper]) return overrides[upper];
  return titleCase(raw);
}

function normalizeBullets(value) {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value
      .map((item) => String(isPlainObject(item) ? item.text || item.label || item.description || formatSignalValue(item) : item || "").trim())
      .filter(Boolean);
  }
  if (typeof value === "string") {
    return value
      .split(/\n+/)
      .map((line) => line.replace(/^[•*-]\s*/, "").trim())
      .filter(Boolean);
  }
  if (isPlainObject(value)) {
    return Object.values(value)
      .map((item) => String(item || "").trim())
      .filter(Boolean);
  }
  const text = String(value || "").trim();
  return text ? [text] : [];
}

function normalizeEvidenceText(value) {
  if (value == null) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) {
    return value.map((item) => normalizeEvidenceText(item)).filter(Boolean).join(", ");
  }
  if (isPlainObject(value)) {
    return String(value.text || value.quote || value.evidence || value.example || value.label || value.signal || value.title || "").trim();
  }
  return String(value || "").trim();
}

function collectEvidencePieces(source) {
  const pieces = [];
  const pushUnique = (value) => {
    const text = String(value || "").trim();
    if (!text) return;
    if (!pieces.includes(text)) pieces.push(text);
  };

  if (source == null) return pieces;
  if (typeof source === "string" || typeof source === "number" || typeof source === "boolean") {
    pushUnique(source);
    return pieces;
  }
  if (Array.isArray(source)) {
    source.forEach((item) => {
      collectEvidencePieces(item).forEach(pushUnique);
    });
    return pieces;
  }
  if (isPlainObject(source)) {
    if (source.text) pushUnique(source.text);
    if (source.quote) pushUnique(source.quote);
    if (source.evidence) collectEvidencePieces(source.evidence).forEach(pushUnique);
    if (source.example) pushUnique(source.example);
    if (source.examples) collectEvidencePieces(source.examples).forEach(pushUnique);
    if (source.spans) collectEvidencePieces(source.spans).forEach(pushUnique);
    if (source.quotes) collectEvidencePieces(source.quotes).forEach(pushUnique);
    if (source.items) collectEvidencePieces(source.items).forEach(pushUnique);
    if (source.highlights) collectEvidencePieces(source.highlights).forEach(pushUnique);
    return pieces;
  }
  pushUnique(normalizeEvidenceText(source));
  return pieces;
}

function collectWritingSignalGroups(payload) {
  const groups = new Map();
  const notes = [];

  const ensureGroup = (label) => {
    const cleanLabel = normalizeSignalLabel(label) || "Signal";
    const key = cleanLabel.toLowerCase();
    if (!groups.has(key)) {
      groups.set(key, {
        label: cleanLabel,
        evidence: [],
        countHint: 0,
      });
    }
    return groups.get(key);
  };

  const addEvidence = (label, source, countHint = null) => {
    const group = ensureGroup(label);
    const pieces = collectEvidencePieces(source);
    pieces.forEach((piece) => {
      if (!group.evidence.includes(piece)) group.evidence.push(piece);
    });
    const nextCount = Number.isFinite(Number(countHint)) ? Number(countHint) : 0;
    if (nextCount > group.countHint) group.countHint = nextCount;
    if (group.evidence.length > group.countHint) group.countHint = group.evidence.length;
  };

  const addSignalObject = (signalSource, fallbackLabel) => {
    if (!isPlainObject(signalSource)) return;
    const label = signalSource.label || signalSource.name || signalSource.title || signalSource.signal || fallbackLabel;
    const evidenceSource =
      signalSource.spans ||
      signalSource.quotes ||
      signalSource.items ||
      signalSource.highlights ||
      signalSource.evidence ||
      signalSource.examples ||
      signalSource.text ||
      [];
    const countHint =
      signalSource.count ??
      signalSource.example_count ??
      signalSource.examples_count ??
      signalSource.spans?.length ??
      signalSource.highlights?.length ??
      signalSource.evidence?.length ??
      signalSource.examples?.length ??
      null;
    addEvidence(label, evidenceSource, countHint);
  };

  const addSource = (source) => {
    if (!source) return;
    if (Array.isArray(source)) {
      source.forEach((item, index) => {
        if (typeof item === "string") {
          addEvidence(item, [item], 1);
          return;
        }
        if (isPlainObject(item)) {
          addSignalObject(item, item.label || item.signal || `Signal ${index + 1}`);
          return;
        }
        addEvidence(`Signal ${index + 1}`, [item], 1);
      });
      return;
    }
    if (!isPlainObject(source)) {
      addEvidence("Signal", [source], 1);
      return;
    }

    if (Array.isArray(source.writing_signals)) {
      addSource(source.writing_signals);
    }
    if (isPlainObject(source.signals)) {
      Object.entries(source.signals).forEach(([key, value]) => {
        if (isPlainObject(value)) {
          addSignalObject(value, key);
          return;
        }
        addEvidence(key, [value], 1);
      });
    }
    if (Array.isArray(source.basic_annotations)) {
      source.basic_annotations.forEach((item) => {
        if (isPlainObject(item)) addEvidence(item.label || item.signal || "Signal", item.text || item, 1);
      });
    }
    if (Array.isArray(source.enriched_annotations)) {
      source.enriched_annotations.forEach((item) => {
        if (isPlainObject(item)) addEvidence(item.label || item.signal || "Signal", item.text || item, 1);
      });
    }
    if (isPlainObject(source.summary)) {
      const summary = source.summary;
      if (Array.isArray(summary.selected_signals)) {
        summary.selected_signals.forEach((signalName) => {
          ensureGroup(signalName);
        });
      }
    }
  };

  addSource(payload?.writing_signals);
  addSource(payload?.feedback?.writing_signals);
  addSource(payload?.evaluation?.writing_signals);
  addSource(payload?.signals);

  if (Array.isArray(payload?.signal_feedback_lines)) {
    payload.signal_feedback_lines.forEach((line) => {
      const text = String(line || "").trim();
      if (text) notes.push(text);
    });
  }

  return {
    groups: Array.from(groups.values()).sort((a, b) => a.label.localeCompare(b.label)),
    notes,
  };
}

async function fetchJson(url, options) {
  const response = await fetch(url, options);
  const json = await response.json().catch(() => null);
  return { response, json };
}

function LessonContextCard({ lessonContext, selectedWeek, selectedLesson }) {
  if (!lessonContext) {
    return (
      <div style={styles.card}>
        <div style={styles.sectionLabel}>Lesson context</div>
        <div style={styles.muted}>Lesson context unavailable.</div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.sectionLabel}>Lesson context</div>
      <div style={styles.contextTitle}>{lessonContext.title || lessonContext.topic || "Lesson"}</div>
      <div style={styles.contextItem}>Lesson ID: {lessonContext.id || toTitle(selectedWeek, selectedLesson)}</div>
      <div style={styles.contextItem}>Objective: {lessonContext.objective || lessonContext.learning_objective || "—"}</div>
      <div style={styles.contextItem}>Prompt: {lessonContext.task || "—"}</div>
      <div style={styles.contextBlock}>
        <div style={styles.contextItemLabel}>Success criteria</div>
        {normalizeBullets(lessonContext.success_criteria).length ? (
          <ul style={styles.bulletList}>
            {normalizeBullets(lessonContext.success_criteria).map((item, index) => (
              <li key={`${item}-${index}`} style={styles.bulletItem}>
                {item}
              </li>
            ))}
          </ul>
        ) : (
          <div style={styles.contextItem}>—</div>
        )}
      </div>
      <div style={styles.contextItem}>Rubric focus: {lessonContext.rubric_focus || "—"}</div>
      <div style={styles.contextItem}>Lesson mode: {lessonContext.lesson_mode?.mode || lessonContext.lesson_mode || "—"}</div>
    </div>
  );
}

function InfoPill({ label, value }) {
  return (
    <div style={styles.infoPill}>
      <div style={styles.infoPillLabel}>{label}</div>
      <div style={styles.infoPillValue}>{value || "—"}</div>
    </div>
  );
}

export default function TutorReviewWorkspace() {
  const { studentId, weekId, lessonId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [curriculum, setCurriculum] = useState(null);
  const [notes, setNotes] = useState([]);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingNoteId, setDeletingNoteId] = useState(null);
  const [error, setError] = useState("");
  const [draftSaved, setDraftSaved] = useState(false);
  const [openSignals, setOpenSignals] = useState([]);

  const selectedWeek = Number(location.state?.selectedWeek || weekId) || null;
  const selectedLesson = Number(location.state?.selectedLesson || lessonId) || null;
  const studentName = location.state?.studentName || studentId;
  const storageKey = `tutor_review_draft:${studentId}:W${selectedWeek}:L${selectedLesson}`;

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError("");
      try {
        const [dashboardResult, notesResult, curriculumResult] = await Promise.all([
          fetchJson(`${API}/api/tutor/student/${studentId}/dashboard_v2`, {
            cache: "no-store",
            headers: { "X-User": getAuthUserHeader() },
          }),
          fetchJson(`${API}/api/student/${studentId}/tutor-notes`, { cache: "no-store" }).catch(() => ({
            response: { ok: false },
            json: null,
          })),
          fetchJson(`${API}/curriculum`, { cache: "no-store" }).catch(() => ({
            response: { ok: false },
            json: null,
          })),
        ]);

        if (cancelled) return;

        if (!dashboardResult.response.ok) {
          throw new Error(dashboardResult.json?.detail || "Unable to load review workspace.");
        }

        const dashboardData = dashboardResult.json || null;
        const notesData = notesResult.response.ok ? notesResult.json : null;
        const curriculumData = curriculumResult.response.ok ? curriculumResult.json : null;

        setDashboard(dashboardData);
        setNotes(Array.isArray(notesData?.notes) ? notesData.notes : []);
        setCurriculum(curriculumData || null);

        const savedDraft = localStorage.getItem(storageKey);
        if (savedDraft != null) {
          setNote(savedDraft);
        } else {
          const existingNote = Array.isArray(notesData?.notes)
            ? notesData.notes.find(
                (item) =>
                  String(item.week) === String(selectedWeek) && String(item.lesson) === String(selectedLesson)
              )
            : null;
          setNote(existingNote?.note || "");
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Unable to load review workspace.");
          setDashboard(null);
          setNotes([]);
          setCurriculum(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [studentId, selectedLesson, selectedWeek, storageKey]);

  const curriculumMap = useMemo(() => buildCurriculumLessonMap(curriculum), [curriculum]);
  const selectedLessonData = useMemo(() => {
    if (!dashboard?.weeks) return null;
    for (const week of dashboard.weeks) {
      if (Number(week?.week) !== Number(selectedWeek)) continue;
      const lesson = (week.lessons || []).find((item) => Number(item.lesson) === Number(selectedLesson));
      if (lesson) return lesson;
    }
    return null;
  }, [dashboard, selectedLesson, selectedWeek]);

  const lessonContext = useMemo(() => {
    const lessonIdKey = toTitle(selectedWeek, selectedLesson);
    return curriculumMap.get(lessonIdKey) || null;
  }, [curriculumMap, selectedLesson, selectedWeek]);

  const currentNote = String(note ?? "");
  const trimmedNote = currentNote.trim();
  const selectedAttempts = Array.isArray(selectedLessonData?.attempts) ? selectedLessonData.attempts : [];
  const bestAttempt = selectedLessonData?.final_best || selectedAttempts[selectedAttempts.length - 1] || null;
  const feedback = selectedLessonData?.feedback || null;
  const resultJson = parseMaybeJson(selectedLessonData?.result_json) || {};
  const writingSignalPayload = {
    writing_signals: parseMaybeJson(resultJson?.writing_signals) || resultJson?.writing_signals || {},
    signal_feedback_lines: Array.isArray(resultJson?.signal_feedback_lines) ? resultJson.signal_feedback_lines : [],
    feedback: resultJson?.feedback,
    evaluation: resultJson?.evaluation,
    signals: resultJson?.signals,
  };
  const { groups: writingSignalGroups } = collectWritingSignalGroups(writingSignalPayload);
  const summaryScore =
    feedback?.score ??
    selectedLessonData?.score ??
    resultJson?.score ??
    selectedLessonData?.overall ??
    selectedLessonData?.rubric_score ??
    bestAttempt?.score ??
    bestAttempt?.overall ??
    null;
  const coachFeedback = safeText(
    resultJson?.student_feedback?.coach_feedback_paragraph ||
      resultJson?.student_feedback?.feedback_text ||
      resultJson?.feedback_text ||
      feedback?.comments ||
      feedback?.coach_feedback ||
      feedback?.summary
  );
  const submissionText = safeText(selectedLessonData?.draft_text || selectedLessonData?.final_text);
  const submittedAt = selectedLessonData?.created_at || selectedLessonData?.submitted_at || selectedLessonData?.latest_activity || null;
  const feedbackGeneratedAt = feedback?.feedback_generated_at || resultJson?.feedback?.feedback_generated_at || null;
  const formattedFeedbackTimestamp = feedbackGeneratedAt
    ? (() => {
        const parts = new Intl.DateTimeFormat("en-US", {
          timeZone: "Asia/Karachi", day: "numeric", month: "long", year: "numeric",
          hour: "numeric", minute: "2-digit", hour12: true,
        }).formatToParts(new Date(feedbackGeneratedAt));
        const get = (type) => parts.find((part) => part.type === type)?.value || "";
        return `${get("day")} ${get("month")} ${get("year")} · ${get("hour").replace(/^0/, "")}:${get("minute")} ${get("dayPeriod").toUpperCase()} PKT`;
      })()
    : null;
  const matchedNotes = notes.filter(
    (item) => String(item.week) === String(selectedWeek) && String(item.lesson) === String(selectedLesson)
  );
  const latestTutorNote = matchedNotes[0] || null;
  const latestTutorNoteText = safeText(latestTutorNote?.note);
  const hasDraftNote = trimmedNote.length > 0;
  const isDuplicateNote = hasDraftNote && trimmedNote === latestTutorNoteText;
  const canSendNote = hasDraftNote && !isDuplicateNote;
  const saveDraft = () => {
    localStorage.setItem(storageKey, trimmedNote);
    setDraftSaved(true);
    window.setTimeout(() => setDraftSaved(false), 1500);
  };

  const saveAndNotify = async () => {
    if (!canSendNote) return;
    setSaving(true);
    setError("");
    try {
      const res = await fetch(`${API}/tutor/notes/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            student_id: studentId,
            week: selectedWeek,
            lesson: selectedLesson,
            notes: trimmedNote,
          }),
      });
      if (!res.ok) throw new Error(`Save failed (${res.status})`);
      localStorage.removeItem(storageKey);
      setNotes((current) => [
        {
          id: Date.now(),
          student_id: studentId,
          note: trimmedNote,
          created_at: new Date().toISOString(),
          week: selectedWeek,
          lesson: selectedLesson,
        },
        ...current,
      ]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to save tutor note.");
    } finally {
      setSaving(false);
    }
  };

  const deleteTutorNote = async (item) => {
    if (!item?.id) return;
    const confirmed = window.confirm("Delete this tutor note? This will remove it from the student notification feed.");
    if (!confirmed) return;

    setDeletingNoteId(item.id);
    setError("");
    try {
      const res = await fetch(`${API}/api/student/${studentId}/tutor-notes/${item.id}`, {
        method: "DELETE",
        headers: { "X-User": getAuthUserHeader() },
      });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      setNotes((current) => current.filter((noteItem) => noteItem.id !== item.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to delete tutor note.");
    } finally {
      setDeletingNoteId(null);
    }
  };

  const toggleSignal = (label) => {
    const key = normalizeSignalLabel(label);
    setOpenSignals((current) =>
      current.includes(key) ? current.filter((item) => item !== key) : [...current, key]
    );
  };

  return (
    <section style={styles.page}>
      <div style={styles.pageShell}>
        <header style={styles.header}>
          <div style={styles.headerCopy}>
            <div style={styles.eyebrow}>Review & Respond</div>
            <h1 style={styles.title}>{studentName}</h1>
            <div style={styles.subtitle}>
              {selectedWeek && selectedLesson
                ? `${toTitle(selectedWeek, selectedLesson)} · ${lessonContext?.title || lessonContext?.topic || "Lesson"}`
                : "Select a lesson"}
            </div>
            <div style={styles.headerMetaRow}>
              <InfoPill label="Student ID" value={studentId} />
              <InfoPill
                label="Lesson"
                value={selectedWeek && selectedLesson ? toTitle(selectedWeek, selectedLesson) : "—"}
              />
              <InfoPill label="Submitted" value={submittedAt ? new Date(submittedAt).toLocaleString() : "—"} />
            </div>
          </div>
          <button type="button" style={styles.backButton} onClick={() => navigate("/tutor/needs-review")}>
            Back to Needs Review
          </button>
        </header>

        {loading ? <div style={styles.loading}>Loading review workspace...</div> : null}
        {error ? <div style={styles.error}>{error}</div> : null}

        {!loading && dashboard ? (
          <div style={styles.contentColumn}>
            <LessonContextCard lessonContext={lessonContext} selectedWeek={selectedWeek} selectedLesson={selectedLesson} />

            <div style={styles.card}>
              <div style={styles.sectionLabel}>Student submission</div>
              <div style={styles.submission}>{submissionText || `No submission found for ${toTitle(selectedWeek, selectedLesson)}.`}</div>
              <div style={styles.metaRow}>
                <span>Attempt: {bestAttempt?.attempt ?? selectedLessonData?.attempt ?? "—"}</span>
                <span>Word count: {wordCount(submissionText) || "—"}</span>
              </div>
            </div>

            <div style={styles.card}>
              <div style={styles.sectionLabel}>Coach feedback</div>
              {formattedFeedbackTimestamp ? <div>{formattedFeedbackTimestamp}</div> : null}
              <div>Use this to improve your next draft.</div>
              <div style={styles.scoreGrid}>
                <InfoPill
                  label="Overall score"
                  value={summaryScore != null ? summaryScore : "—"}
                />
                <InfoPill
                  label="Creativity"
                  value={feedback?.creativity_score ?? resultJson?.feedback?.creativity ?? bestAttempt?.creativity ?? "—"}
                />
                <InfoPill
                  label="Clarity"
                  value={feedback?.clarity_score ?? resultJson?.feedback?.clarity ?? bestAttempt?.clarity ?? "—"}
                />
                <InfoPill
                  label="Task score"
                  value={feedback?.task_score ?? resultJson?.feedback?.task_completion ?? bestAttempt?.task_completion ?? "—"}
                />
              </div>
              <div style={styles.paragraph}>{coachFeedback || "No AI/Coach feedback yet."}</div>
              {writingSignalGroups.length ? (
                <div style={styles.signalSection}>
                  <div style={styles.sectionLabelSmall}>Writing signals</div>
                  <div style={styles.signalList}>
                    {writingSignalGroups.map((item) => (
                      <div key={item.label} style={styles.signalAccordion}>
                        <button
                          type="button"
                          style={styles.signalSummary}
                          onClick={() => toggleSignal(item.label)}
                          aria-expanded={openSignals.includes(normalizeSignalLabel(item.label)) ? "true" : "false"}
                        >
                          <span style={styles.signalSummaryTitle}>{item.label}</span>
                          <span style={styles.signalSummaryCount}>
                            {item.evidence.length
                              ? `${item.evidence.length} example${item.evidence.length === 1 ? "" : "s"}`
                              : item.countHint
                              ? `${item.countHint} example${item.countHint === 1 ? "" : "s"}`
                              : "No evidence text available"}
                          </span>
                        </button>
                        {openSignals.includes(normalizeSignalLabel(item.label)) ? (
                          <div style={styles.signalBody}>
                            {item.evidence.length ? (
                              <ul style={styles.signalEvidenceList}>
                                {item.evidence.map((evidence, index) => (
                                  <li key={`${item.label}-${index}`} style={styles.signalEvidenceItem}>
                                    “{evidence}”
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <div style={styles.signalNoEvidence}>No evidence text available.</div>
                            )}
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>

            <div style={styles.card}>
              <div style={styles.sectionLabel}>Tutor guidance</div>
              <textarea
                value={currentNote}
                onChange={(e) => setNote(e.target.value)}
                style={styles.textarea}
                placeholder="Write tutor guidance for this student..."
              />
              <div style={styles.buttonRow}>
                <button type="button" style={styles.secondaryButton} onClick={saveDraft}>
                  Save Draft
                </button>
                <button
                  type="button"
                  style={styles.primaryButton}
                  onClick={saveAndNotify}
                  disabled={saving || !canSendNote}
                >
                  {saving
                    ? "Saving..."
                    : !hasDraftNote
                    ? "Write a note to notify"
                    : isDuplicateNote
                    ? "Already sent"
                    : "Save & Notify Student"}
                </button>
              </div>
              {draftSaved ? <div style={styles.saved}>Draft saved locally.</div> : null}
            </div>

            <div style={styles.card}>
              <div style={styles.sectionLabel}>Previous Tutor Notes</div>
              {matchedNotes.length ? (
                <div style={styles.noteList}>
                  {matchedNotes.map((item) => (
                    <div key={item.id} style={styles.noteItem}>
                      <div style={styles.paragraph}>{item.note}</div>
                      <div style={styles.noteFooter}>
                        <div style={styles.noteMeta}>{item.created_at}</div>
                        <button
                          type="button"
                          style={styles.deleteButton}
                          onClick={() => deleteTutorNote(item)}
                          disabled={deletingNoteId === item.id}
                        >
                          {deletingNoteId === item.id ? "Deleting..." : "Delete"}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={styles.muted}>No tutor note yet.</div>
              )}
            </div>

          </div>
        ) : null}

        {!loading && !dashboard && !error ? <div style={styles.muted}>Review data unavailable.</div> : null}
      </div>
    </section>
  );
}

const styles = {
  page: {
    padding: "24px 0 36px",
  },
  pageShell: {
    maxWidth: 1360,
    margin: "0 auto",
    padding: "0 20px",
    display: "grid",
    gap: 18,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    gap: 16,
    alignItems: "flex-start",
  },
  eyebrow: {
    color: "#93c5fd",
    textTransform: "uppercase",
    letterSpacing: ".08em",
    fontSize: 12,
    fontWeight: 700,
  },
  title: {
    margin: "6px 0 0",
    color: "var(--text)",
  },
  headerCopy: {
    minWidth: 0,
  },
  subtitle: {
    marginTop: 6,
    color: "var(--muted)",
  },
  headerMetaRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    marginTop: 14,
  },
  infoPill: {
    minWidth: 160,
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid #1f2a3a",
    background: "#0e151f",
    display: "grid",
    gap: 4,
  },
  infoPillLabel: {
    color: "var(--muted)",
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: ".08em",
    fontWeight: 700,
  },
  infoPillValue: {
    color: "var(--text)",
    fontWeight: 700,
    lineHeight: 1.4,
    overflowWrap: "anywhere",
  },
  backButton: {
    appearance: "none",
    border: "1px solid rgba(148,163,184,.28)",
    background: "rgba(255,255,255,.06)",
    color: "var(--text)",
    padding: "10px 14px",
    borderRadius: 10,
    cursor: "pointer",
  },
  loading: { color: "var(--muted)" },
  error: {
    color: "#fecaca",
    background: "rgba(127, 29, 29, .35)",
    border: "1px solid rgba(248, 113, 113, .35)",
    borderRadius: 12,
    padding: 14,
  },
  contentColumn: {
    width: "100%",
    maxWidth: 1000,
    margin: "0 auto",
    display: "grid",
    gap: 14,
  },
  card: {
    background: "rgba(14,19,27,.92)",
    border: "1px solid #1a2230",
    borderRadius: 16,
    padding: 16,
    display: "grid",
    gap: 10,
  },
  sectionLabel: {
    color: "#93c5fd",
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: ".08em",
    fontWeight: 700,
  },
  sectionLabelSmall: {
    color: "var(--muted)",
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: ".08em",
    fontWeight: 700,
  },
  submission: { color: "var(--text)", whiteSpace: "pre-wrap", lineHeight: 1.7 },
  metaRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    color: "var(--muted)",
    fontSize: 12,
  },
  scoreGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 10,
  },
  paragraph: { color: "var(--text)", lineHeight: 1.7, whiteSpace: "pre-wrap" },
  signalSection: { display: "grid", gap: 8 },
  signalList: {
    display: "grid",
    gap: 8,
  },
  signalAccordion: {
    borderRadius: 12,
    border: "1px solid #1f2a3a",
    background: "#0e151f",
    overflow: "hidden",
  },
  signalSummary: {
    appearance: "none",
    border: 0,
    background: "transparent",
    cursor: "pointer",
    padding: "12px 14px",
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "center",
    color: "var(--text)",
    width: "100%",
    textAlign: "left",
  },
  signalSummaryTitle: {
    fontWeight: 800,
    overflowWrap: "anywhere",
  },
  signalSummaryCount: {
    color: "var(--muted)",
    fontSize: 12,
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  signalBody: {
    borderTop: "1px solid #1f2a3a",
    padding: "12px 14px 14px",
  },
  signalEvidenceList: {
    margin: 0,
    paddingLeft: 22,
    display: "grid",
    gap: 8,
    color: "var(--text)",
  },
  signalEvidenceItem: {
    lineHeight: 1.6,
    overflowWrap: "anywhere",
  },
  signalNoEvidence: {
    color: "var(--muted)",
    lineHeight: 1.6,
  },
  signalNotesSection: {
    display: "grid",
    gap: 8,
    marginTop: 4,
  },
  signalNotesList: {
    margin: 0,
    paddingLeft: 20,
    display: "grid",
    gap: 8,
    color: "var(--text)",
  },
  signalNotesItem: {
    lineHeight: 1.6,
    overflowWrap: "anywhere",
  },
  signalItem: {
    padding: "10px 12px",
    borderRadius: 10,
    border: "1px solid #1f2a3a",
    background: "#0e151f",
    color: "var(--text)",
    overflowWrap: "anywhere",
    lineHeight: 1.55,
  },
  signalLabel: {
    fontWeight: 800,
  },
  signalDetail: {
    color: "var(--muted)",
  },
  contextTitle: { color: "var(--text)", fontSize: 18, fontWeight: 800 },
  contextItem: { color: "var(--text)", lineHeight: 1.6, overflowWrap: "anywhere" },
  contextItemLabel: {
    color: "var(--muted)",
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: ".08em",
    fontWeight: 700,
    marginBottom: 6,
  },
  contextBlock: {
    display: "grid",
    gap: 4,
  },
  bulletList: {
    margin: "0 0 0 20px",
    padding: 0,
    display: "grid",
    gap: 6,
    color: "var(--text)",
  },
  bulletItem: {
    lineHeight: 1.6,
    overflowWrap: "anywhere",
  },
  textarea: {
    width: "100%",
    minHeight: 190,
    resize: "vertical",
    padding: 12,
    borderRadius: 12,
    border: "1px solid #1f2a3a",
    background: "#0e151f",
    color: "var(--text)",
    fontFamily: "inherit",
    fontSize: 16,
    lineHeight: 1.5,
    letterSpacing: 0,
  },
  buttonRow: { display: "flex", gap: 10, flexWrap: "wrap" },
  primaryButton: {
    appearance: "none",
    border: "1px solid rgba(201,165,106,.55)",
    background: "rgba(201,165,106,.14)",
    color: "#f0e6d4",
    padding: "10px 14px",
    borderRadius: 10,
    cursor: "pointer",
    fontWeight: 800,
  },
  secondaryButton: {
    appearance: "none",
    border: "1px solid rgba(148,163,184,.32)",
    background: "rgba(255,255,255,.06)",
    color: "var(--text)",
    padding: "10px 14px",
    borderRadius: 10,
    cursor: "pointer",
    fontWeight: 700,
  },
  saved: { color: "#86efac", fontSize: 12 },
  noteList: { display: "grid", gap: 10 },
  noteItem: {
    background: "#0e151f",
    border: "1px solid #1f2a3a",
    borderRadius: 12,
    padding: 12,
    display: "grid",
    gap: 8,
  },
  noteFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
  },
  noteMeta: { color: "var(--muted)", fontSize: 12 },
  deleteButton: {
    appearance: "none",
    border: "1px solid rgba(248, 113, 113, .38)",
    background: "rgba(127, 29, 29, .22)",
    color: "#fecaca",
    padding: "8px 12px",
    borderRadius: 10,
    cursor: "pointer",
    fontWeight: 700,
  },
  muted: { color: "var(--muted)" },
};
